import { capitalize } from 'lodash';
import { transparentize } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { UserLiteData } from '../../../../api/ApiTypes';
import { Colors, toRem } from '../../../../ui';
import { Badge } from '../../../../ui/Badge';
import { CoolTableTheme, Table } from '../../../../ui/Table';
import { UserRole } from '../../../PassportModel';
import { Avatar } from '../../../common/Avatar';
import { Copy, Flexer } from '../../../dashboard/elements/LayoutElements';

const CellImage = styled.img<{ width?: any }>`
    width: ${p => toRem(p.width || 1.5)};
    border-radius: .25rem;
`;

const CellContent = styled.div<{ mode?: string }>`
    display: flex;
    flex-direction: ${p => p.mode || 'row'};
    align-items: center;
    gap: .5rem;
`;

export const BadgeCell = ({ value, color }: { value: string, color: string }) => {
    if (!value) return <span></span>;
    return <Flexer flex="row" align="center">
        <Badge color={color} size="lg">{value}</Badge>
    </Flexer>;
};

export const UserCell = ({ value }: { value: UserLiteData }) => {
    if (!value) return <span></span>;
    return <CellContent>
        {/* <Counter size={1.75} unit="rem" color={value.color} photo={value.photo}>
            {value.initials}
        </Counter> */}
        <Avatar.ForUser user={value} size="1.75" />
        <span style={{ fontWeight: 450 }}>{value.name}</span>
    </CellContent>;
};

export const TextCell = ({ value }: { value: any }) => {
    return <Copy flush>{value || ''}</Copy>;
};

export const DoubleTextCell = ({ value1, value2 }: { value1: any, value2?: any }) => {
    return <Flexer flex="column" justify="center">
        <Copy color={Colors.text} flush>{value1 || ''}</Copy>
        {value2 && <Copy size=".75" color={Colors.textLighter} flush>{value2 || ''}</Copy>}
    </Flexer>;
};

export const ImageCell = ({ url, size }: { url: string, size?: any }) => {
    return <CellContent>
        {url && <CellImage src={url} width={size} />}
    </CellContent>;
};

const RoleBadge = styled.div<{ roleName: string }>`
    position: relative;
    top: .1rem;
    margin-right: 1em;
    font-size: .75rem;
    font-weight: 450;
    padding: .2rem .25rem .1rem;
    border-radius: .3rem;
    color: white;
    text-align: center;
    ${p => p.roleName === 'account_owner' ? `background: ${Colors.mystery}` : ''};
    ${p => p.roleName === 'account_admin' ? `background: ${Colors.primary}` : ''};
    ${p => p.roleName === 'account_member' ? `background: ${Colors.positive};` : ''};
    ${p => p.roleName === 'account_guest' ? `background: ${Colors.gray500};` : ''};
`;

export const RoleCell = ({ value }: { value: string }) => {
    if (!value) return <span></span>;
    return <CellContent>
        <RoleBadge roleName={value}>
            {UserRole.lookup(value)?.badge}
        </RoleBadge>
    </CellContent>;
};

const InviteBadge = styled.div<{ value: string }>`
    position: relative;
    top: .1rem;
    margin-right: 1em;
    font-size: .75rem;
    font-weight: 450;
    padding: .2rem .25rem .1rem;
    border-radius: .3rem;
    color: white;
    text-align: center;
    ${p => p.value === 'pending' ? `background: ${Colors.observe}` : ''};
    ${p => p.value === 'sent' ? `background: ${Colors.positive}` : ''};
    ${p => p.value === 'used' ? `background: ${Colors.primary};` : ''};
    ${p => p.value === 'expired' ? `background: ${Colors.gray400};` : ''};
    ${p => p.value === 'rejected' ? `background: ${Colors.negative};` : ''};
`;

export const InviteStateCell = ({ value }: { value: string }) => {
    if (!value) return <span></span>;
    return <CellContent>
        <InviteBadge value={value}>
            {value === 'pending' ? 'Requires Approval' : capitalize(value)}
        </InviteBadge>
    </CellContent>;
};

export const AdminTableTheme = styled(CoolTableTheme)`
    cursor: default !important;

    --row-hover-bg: none;
    --row-active-bg: none;
    --row-selected-bg: ${transparentize(0.5, Colors.hover)} !important;
    --cell-padding: .25rem .5rem;

    tr {
        border-bottom: solid .0625rem ${Colors.border};

        td {
            white-space: nowrap;

            &:first-child {
                padding: .25rem .5rem;
            }
        }
    }

    ${Table.Head} {
        td {
            padding-top: .375rem;
            padding-bottom: .375rem;
        }
    }

    @media (min-width: 80rem) {
        --cell-padding: .25rem 1rem;
    }

    @media (min-width: 120rem) {
        --cell-padding: .25rem 1.5rem;
    }
`;
